import { useState, useEffect, useCallback } from 'preact/hooks'
import { applyTheme } from '../services/themes'
import { loadUserSettings, saveUserSettings } from '../services/api'

const FONT_SIZE_SCALES = [0.85, 0.92, 1.0, 1.1, 1.2] // XS, S, M, L, XL

function applyFontSize(index: number): void {
  const scale = FONT_SIZE_SCALES[index] ?? 1.0
  document.documentElement.style.fontSize = `${scale * 15}px`
}

interface UseThemeResult {
  theme: string
  darkMode: boolean
  fontSizeIndex: number
  setTheme: (theme: string) => void
  setDarkMode: (dark: boolean) => void
  setFontSizeIndex: (index: number) => void
}

export function useTheme(accountReady: boolean): UseThemeResult {
  const [theme, setThemeState] = useState('sea')
  const [darkMode, setDarkModeState] = useState(true)
  const [fontSizeIndex, setFontSizeState] = useState(2)

  // Load saved settings from backend
  useEffect(() => {
    if (!accountReady) return
    loadUserSettings('web').then((settings) => {
      const t = (settings.theme_type as string) || 'sea'
      const d = settings.dark_mode !== undefined ? !!settings.dark_mode : true
      const f = typeof settings.font_size_index === 'number' ? settings.font_size_index : 2
      setThemeState(t)
      setDarkModeState(d)
      setFontSizeState(f)
      applyTheme(t, d)
      applyFontSize(f)
    }).catch(() => {})
  }, [accountReady])

  // Apply theme whenever it changes
  useEffect(() => {
    applyTheme(theme, darkMode)
  }, [theme, darkMode])

  const setTheme = useCallback((t: string) => {
    setThemeState(t)
    applyTheme(t, darkMode)
    saveUserSettings({ theme_type: t }, 'web').catch(() => {})
  }, [darkMode])

  const setDarkMode = useCallback((d: boolean) => {
    setDarkModeState(d)
    applyTheme(theme, d)
    saveUserSettings({ dark_mode: d }, 'web').catch(() => {})
  }, [theme])

  const setFontSizeIndex = useCallback((i: number) => {
    setFontSizeState(i)
    applyFontSize(i)
    saveUserSettings({ font_size_index: i }, 'web').catch(() => {})
  }, [])

  return { theme, darkMode, fontSizeIndex, setTheme, setDarkMode, setFontSizeIndex }
}
